import { createRoot } from 'react-dom/client';
import { setupComponents } from '@didomi/ui-atoms-react';
import { connect } from '@didomi/ui-foundation';
import { ensureIbmPlexSansFonts } from './loadIbmPlexSans';
import { OrgSelectorApp } from './OrgSelectorApp';

function ensureRootElement(): HTMLElement {
  const existing = document.getElementById('root');
  if (existing) return existing;

  const root = document.createElement('div');
  root.id = 'root';
  document.body.appendChild(root);
  return root;
}

let didomiUiReady: Promise<void> | null = null;

async function loadDidomiAtoms(prefix: string): Promise<void> {
  setupComponents(prefix);
  const defineWithPrefix = (defineCustomElements: (...args: any[]) => void) =>
    Promise.resolve(
      (defineCustomElements as unknown as (win: Window, options: { transformTagName: (tagName: string) => string }) => void)(window, {
        transformTagName: (tagName: string) => `${prefix ? `${prefix}-` : ''}${tagName}`,
      }),
    );

  if (import.meta.env.DEV) {
    const { defineCustomElements } = await import('@didomi/ui-atoms/loader');
    await defineWithPrefix(defineCustomElements);
    return;
  }

  const loaderUrl = new URL('./loader.js', import.meta.url).toString();
  const { defineCustomElements } = await import(/* @vite-ignore */ loaderUrl);
  await defineWithPrefix(defineCustomElements);
}

async function ensureDidomiUi(root: HTMLElement): Promise<void> {
  if (didomiUiReady) return didomiUiReady;
  ensureIbmPlexSansFonts();
  root.classList.add('spa-entry-root');
  root.style.fontFamily = "'IBM Plex Sans', sans-serif";
  // Prevent a flash of unstyled content until Twind connects.
  root.setAttribute('hidden', '');
  connect('.spa-entry-root');
  root.removeAttribute('hidden');
  didomiUiReady = loadDidomiAtoms('oauth-ui');
  return didomiUiReady;
}

export async function bootstrapOAuthUi() {
  const root = ensureRootElement();
  await ensureDidomiUi(root);
  createRoot(root).render(<OrgSelectorApp />);
}

// Auto-run when loaded by Admin API callback page.
try {
  void bootstrapOAuthUi();
} catch (e) {
  // Keep failures visible but non-fatal for the callback page.
  // eslint-disable-next-line no-console
  console.error('[oauth-ui] failed to bootstrap', e);
}
